// src/pages/organization/event/ShowEvent.js

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMainPageTitle } from "../../../contexts/PageContext";
import EventHeader from "./components/EventHeader";
import EventMembers from "./components/EventMembers";
import EventActivities from "./components/EventActivities";
import MatchEvent from "./components/MatchEvent";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";


const ShowEvent = () => {
  const { updatePageTitle } = useMainPageTitle();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [stopwatchTime, setStopwatchTime] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [isRunning, setIsRunning] = useState(false);
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [newActivity, setNewActivity] = useState({
    activityTitle: '',
    activityDescription: '',
    activityImage: null
  });

  const calculateTimeLeft = useCallback((dateString) => {
    if (event?.event?.eventStatus === "In Progress") {
      return "Event is in progress";
    }
    const startDate = new Date(dateString);
    const now = new Date();
    const diff = startDate - now;

    // If the event has already passed
    if (diff < 0) {
      return "Event has ended";
    }

    // Convert milliseconds to days, hours, minutes, seconds
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds`;
  }, [event?.event?.eventStatus]);

  useEffect(() => {
    if (event?.event?.startDate) {
      const timer = setInterval(() => {
        setTimeRemaining(calculateTimeLeft(event.event.startDate));
      }, 1000);

      // Cleanup interval on component unmount
      return () => clearInterval(timer);
    }
  }, [event, calculateTimeLeft]);

  useEffect(() => {
    let intervalId;
    if (event?.event?.eventStatus === "In Progress") {
      setIsRunning(true);
      // Calculate initial time from event start
      const startTime = new Date(event.event.startDate).getTime();
      const currentTime = new Date().getTime();
      setStopwatchTime(Math.floor((currentTime - startTime) / 1000));
      
      // Start the stopwatch
      intervalId = setInterval(() => {
        setStopwatchTime(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [event?.event?.eventStatus, event?.event?.startDate]);

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_URL}/event/get-event-by-id/?eventId=${eventId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch event details");
        }

        const data = await response.json();
        setEvent(data);
        setError(null);
        updatePageTitle(data.event.name, "Event Details");
      } catch (err) {
        setError("Failed to load event details. Please try again later.");
        console.error("Error fetching event:", err);
      } finally {
        setLoading(false);
      }
    };

          if (eventId) {
      fetchEventDetails();
    }
  }, [eventId, updatePageTitle]);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setActivityLoading(true);
        const response = await fetch(
          `${API_URL}/event/get-all-activities/?eventId=${eventId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch activities");
        }

        const data = await response.json();
        setActivities(data.activities);
        setActivityError(null);
      } catch (err) {
        setActivityError("Failed to load activities. Please try again later.");
        console.error("Error fetching activities:", err);
      } finally {
        setActivityLoading(false);
      }
    };

    if (eventId) {
      fetchActivities();
    }
  }, [eventId]);

  const formatStopwatchTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleStartEvent = async () => {
    try {
      const response = await fetch(`${API_URL}/event/start-or-end-event/?eventId=${eventId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eventStatus: "In Progress"
        })
      });

      if (!response.ok) {
        throw new Error("Failed to start event");
      }

      // eslint-disable-next-line no-unused-vars
      const data = await response.json();
      
      // Update local state
      setEvent(prev => ({
        ...prev,
        event: {
          ...prev.event,
          eventStatus: "In Progress",
          startDate: new Date().toISOString()
        },
        members: prev.members.map(member => ({
          ...member,
          attendanceStatus: "pending"
        }))
      }));

    } catch (err) {
      setError("Failed to start event. Please try again later.");
      console.error("Error starting event:", err);
    }
  };

  const handleEndEvent = async () => {
    try {
      const response = await fetch(`${API_URL}/event/start-or-end-event/?eventId=${eventId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eventStatus: "Completed"
        })
      });

      if (!response.ok) {
        throw new Error("Failed to end event");
      }

      // eslint-disable-next-line no-unused-vars
      const data = await response.json();
      
      // Update local state
      setEvent(prev => ({
        ...prev,
        event: {
          ...prev.event,
          eventStatus: "Completed",
          endDate: new Date().toISOString()
        },
        members: prev.members.map(member => ({
          ...member,
          attendanceStatus: member.attendanceStatus === "pending" ? "absent" : member.attendanceStatus
        }))
      }));

    } catch (err) {
      setError("Failed to end event. Please try again later.");
      console.error("Error ending event:", err);
    }
  };

  const handleDeleteEvent = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this event? This action cannot be undone."
      )
    ) {
      try {
        const response = await fetch(
          `${API_URL}/event/delete-event-by-id/?eventId=${eventId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete event");
        }

        navigate("/organization/events");
      } catch (err) {
        setError("Failed to delete event. Please try again later.");
        console.error("Error deleting event:", err);
      }
    }
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formData = new FormData();
      formData.append('eventId', eventId);
      formData.append('activityTitle', newActivity.activityTitle);
      formData.append('activityDescription', newActivity.activityDescription);
      formData.append('image', newActivity.activityImage);

      const response = await fetch(`${API_URL}/event/add-activity-to-event`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add activity");
      }

      const data = await response.json();
      setActivities(prev => [...prev, data.activity]);
      setNewActivity({
        activityTitle: '',
        activityDescription: '',
        activityImage: null
      });
      setActivityError(null);
    } catch (err) {
      setActivityError(err.message || "Failed to add activity. Please try again later.");
      console.error("Error adding activity:", err);
    }
  };

  const handleAttendanceStatusChange = async (memberId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/event/change-member-attendance-status-from-organization`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eventId,
          memberId,
          status: newStatus
        })
      });

      if (!response.ok) {
        throw new Error("Failed to update attendance status");
      }

      // Update local state to reflect the change
      setEvent(prev => ({
        ...prev,
        members: prev.members.map(member => 
          member.userId === memberId 
            ? {...member, attendanceStatus: newStatus}
            : member
        )
      }));

    } catch (err) {
      setError("Failed to update attendance status. Please try again later.");
      console.error("Error updating attendance status:", err);
    }
  };

  const handleScoreUpdate = async (teamId, newScore) => {
    try {
      const response = await fetch(`${API_URL}/event/update-match-score`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eventId,
          teamId,
          score: newScore
        })
      });

      if (!response.ok) {
        throw new Error("Failed to update score");
      }

      const data = await response.json();
      setEvent(prev => ({
        ...prev,
        metaData: {
          ...prev.metaData,
          score: data.score
        }
      }));
    } catch (err) {
      setError("Failed to update score. Please try again later.");
      console.error("Error updating score:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  if (!event) {
    return <div className="text-center py-8">Event not found</div>;
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <EventHeader 
          event={event}
          timeRemaining={timeRemaining}
          stopwatchTime={stopwatchTime}
          formatStopwatchTime={formatStopwatchTime}
          formatDate={formatDate}
        />

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content - Left Side */}
            <div className="lg:col-span-2 space-y-8">
              {/* Status and Date */}
              <div className="flex flex-wrap gap-4 items-center">
                <span
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    event.eventStatus === "Upcoming"
                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                      : event.eventStatus === "In Progress"
                      ? "bg-green-50 text-green-700 border border-green-200"
                      : "bg-gray-50 text-gray-700 border border-gray-200"
                  }`}
                >
                  {event.event.eventStatus}
                </span>
                <span className="flex items-center text-gray-600">
                  <svg
                    className="h-5 w-5 mr-2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Started: {formatDate(event.event.startDate)}
                </span>
                {event?.event?.eventStatus === "Completed" && event?.event?.endDate && (
                  <span className="flex items-center text-gray-600">
                    <svg
                      className="h-5 w-5 mr-2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                    Ended: {formatDate(event.event.endDate)}
                  </span>
                )}
              </div>
              {/* time is left by subtracting start date from current date */}
              <div className="flex flex-wrap gap-4 items-center">
                <span className="text-gray-600">Time Left</span>
                <span className="font-medium text-gray-900">
                  {timeRemaining}
                </span>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Description
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {event.event.description}
                </p>
              </div>

              {/* Match Event Section */}
              {event?.event?.eventType === "Match" && (
                <MatchEvent 
                  event={event}
                  handleScoreUpdate={handleScoreUpdate}
                  handleAttendanceStatusChange={handleAttendanceStatusChange}
                />
              )}

              {/* Members Section - Only show for non-match events */}
              {event?.event?.eventType !== "Match" && (
                <EventMembers 
                  event={event}
                  handleAttendanceStatusChange={handleAttendanceStatusChange}
                />
              )}

              {/* Activities Section */}
              <EventActivities 
                event={event}
                activities={activities}
                activityLoading={activityLoading}
                activityError={activityError}
                newActivity={newActivity}
                setNewActivity={setNewActivity}
                handleActivitySubmit={handleActivitySubmit}
              />
            </div>

            {/* Sidebar - Right Side */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-xl p-6 space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Event Details
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Event Type</span>
                      <span className="font-medium text-gray-900">
                        {event.event.eventType}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Created At</span>
                      <span className="font-medium text-gray-900">
                        {formatDate(event.event.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {event.metaData && event.metaData.score && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Score Details
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Default Score</span>
                        <span className="font-medium text-gray-900">
                          {event.metaData.score.default}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200 space-y-3">
                  {event.event.eventStatus === "Upcoming" && (
                    <button
                      onClick={handleStartEvent}
                      className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium flex items-center justify-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      Start Event
                    </button>
                  )}
                  {event.event.eventStatus === "In Progress" && (
                    <button
                      onClick={handleEndEvent}
                      className="w-full bg-yellow-600 text-white py-3 rounded-lg hover:bg-yellow-700 transition-colors duration-200 font-medium flex items-center justify-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                      </svg>
                      End Event
                    </button>
                  )}
                  {event.event.eventStatus !== "Completed" && (
                    <button
                      onClick={() => navigate(`/organization/event/edit/${eventId}`)}
                      className="w-full bg-[#F15213] text-white py-3 rounded-lg hover:bg-[#d14711] transition-colors duration-200 font-medium"
                    >
                      Update Event
                    </button>
                  )}
                  <button
                    onClick={handleDeleteEvent}
                    className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
                  >
                    Delete Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowEvent;
